// src/pages/TestsPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTestContext } from '../context/TestContext';
import { useLang } from '../context/LangContext';
import { ADMIN_LOGIN_PATH } from '../constants';
import { exportTest, importTest } from '../api/tests';
import { getTestsDisplay, saveTestsDisplay, TestsDisplaySetting } from '../api/settings';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { fadeIn, slideInLeft } from '../utils/animations';

const TestsPageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.lg}`};
  background-color: ${({ theme }) => theme.colors.background};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

type TestsGridProps = {
  $scale: number;
};

const TestsGrid = styled.div<TestsGridProps>`
  display: grid;
  /* Переменная масштаба карточек: задаётся на гриде (значение вводит админ),
     применяется к размерам/шрифтам/паддингам карточек и их кнопок.*/
  --card-scale: ${({ $scale }: TestsGridProps) => $scale};
  grid-template-columns: repeat(auto-fill, calc(360px * var(--card-scale)));
  gap: calc(${({ theme }) => theme.spacing.lg} * var(--card-scale));
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

type TestCardContainerProps = {
  $index: number;
};

const TestCardContainer = styled(Card)<TestCardContainerProps>`
  position: relative;
  animation: ${slideInLeft} 0.5s ease-out;
  animation-delay: ${({ $index }) => $index * 0.1}s;
  display: flex;
  flex-direction: column;
  width: calc(360px * var(--card-scale));
  height: calc(460px * var(--card-scale));
  overflow: hidden;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    height: auto;
  }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const TestHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: start;
  gap: ${({ theme }) => theme.spacing.sm};
  min-width: 0;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TestTitle = styled.h2`
  font-size: calc(1.5rem * var(--card-scale));
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: calc(${({ theme }) => theme.spacing.xs} * var(--card-scale));
  font-weight: 600;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  transition: color 0.2s;
`;

const EditIconButton = styled(IconButton)`
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const DeleteIconButton = styled(IconButton)`
  &:hover { color: #e53e3e; }
`;

// Панель управления отображением карточек (только для админа).
const DisplayControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.cardBackground};
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ScaleInput = styled.input`
  width: 200px;
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: grab;
  user-select: none;
  &:active { cursor: grabbing; }
`;

const OrderInput = styled.input`
  width: 42px;
  text-align: center;
  padding: 2px 4px;
  font-size: 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const TestInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: calc(0.95rem * var(--card-scale));
  color: ${({ theme }) => theme.colors.textLight};

  span:first-child {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: calc(${({ theme }) => theme.spacing.md} * var(--card-scale));
  line-height: 1.6;
  font-size: calc(0.95rem * var(--card-scale));
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StartButton = styled(Button)`
  width: 100%;
  padding: calc(${({ theme }) => theme.spacing.md} * var(--card-scale)) calc(${({ theme }) => theme.spacing.lg} * var(--card-scale));
`;

const AdminActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 4rem 0;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
  font-size: 1.2rem;
  margin: 4rem 0;
`;

/* ── Toast ── */
const ToastMsg = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  padding: 12px 24px;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 0.9rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  animation: fadeInUp 0.3s ease-out;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

/* ── Confirm modal ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const ConfirmModal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const TestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { tests, loading, error, fetchTests, deleteTest, publishTest, unpublishTest } = useTestContext();
  const { t } = useLang();

  const [toastMsg, setToastMsg] = useState('');
  const [confirmState, setConfirmState] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Отображение карточек: масштаб и порядок (из app_settings, общий для админа и гостя).
  const [cardScale, setCardScale] = useState<number>(1.0);
  const [orderIds, setOrderIds] = useState<number[]>([]);
  const [savingDisplay, setSavingDisplay] = useState(false);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const d = await getTestsDisplay();
        setCardScale(d.card_scale);
        setOrderIds(d.order_ids);
      } catch {
        // настройки не загрузились — остаются дефолтные (масштаб 1, порядок по id)
      }
    })();
  }, []);

  useEffect(() => {
    if (!toastMsg) return;
    const timer = window.setTimeout(() => setToastMsg(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const handleStartTest = (testId: number) => {
    const test = tests.find((t) => t.id === testId);
    if (test) {
      navigate(`/test/${testId}`);
    }
  };

  const handleEditTest = (testId: number) => {
    const test = tests.find((t) => t.id === testId);
    if (test) {
      navigate(`/test-editor/${testId}`);
    }
  };

  const handleCreateTest = () => {
    navigate('/test-editor/new');
  };

  const handleDeleteTest = (testId: number, title: string) => {
    setConfirmState({
      message: `${t('deleteConfirmPrefix')} «${title}»${t('deleteConfirmSuffix')}`,
      onConfirm: async () => {
        await deleteTest(testId);
      },
    });
  };

  const handlePublishTest = async (testId: number) => {
    await publishTest(testId);
  };

  const handleUnpublishTest = async (testId: number) => {
    await unpublishTest(testId);
  };

  const handleExportTest = async (testId: number, title: string) => {
    await exportTest(testId, title);
  };

  const handleImportTest = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await importTest(file);
      await fetchTests();
    } catch {
      setToastMsg(t('importError'));
    }
    e.target.value = '';
  };

  const handleLogout = () => {
    logout();
    navigate(ADMIN_LOGIN_PATH);
  };

  // ── Настройки отображения карточек ──────────────────────────────
  const MIN_SCALE = 0.6;
  const MAX_SCALE = 1.6;

  const rescaleGrid = (candidate: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, candidate));

  // Порядок карточек: те, что в order_ids, идут в нём; новые (без order) — в конец по id.
  const sortedTests = useCallback(() => {
    const byId = new Map(tests.map((t) => [t.id, t]));
    const result: typeof tests = [];
    for (const id of orderIds) {
      const t = byId.get(id);
      if (t) { byId.delete(id); result.push(t); }
    }
    for (const t of tests) {
      if (byId.has(t.id)) result.push(t);
    }
    return result;
  }, [tests, orderIds]);

  const saveDisplay = async (scale: number, ids: number[]) => {
    setSavingDisplay(true);
    try {
      const saved = await saveTestsDisplay({ card_scale: scale, order_ids: ids });
      setCardScale(saved.card_scale);
      setOrderIds(saved.order_ids);
      setToastMsg(t('testsDisplaySaved'));
    } catch {
      setToastMsg(t('testsDisplaySaveError'));
    } finally {
      setSavingDisplay(false);
    }
  };

  // Админ меняет ползунок масштаба (сохраняется на сервере).
  const commitScale = (value: number) => {
    const v = rescaleGrid(value);
    setCardScale(v);
    void saveDisplay(v, orderIds);
  };

  // Перестановка drag&drop: orderIds = актуальный порядок отображения.
  const handleDragStart = (id: number) => setDraggedId(id);
  const handleDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    if (id !== dragOverId) setDragOverId(id);
  };
  const handleDrop = (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    setDragOverId(null);
    const srcId = draggedId;
    setDraggedId(null);
    if (srcId === null || srcId === targetId) return;
    const ids = sortedTests().map((x) => x.id);
    const from = ids.indexOf(srcId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, srcId);
    setOrderIds(ids);
    void saveDisplay(cardScale, ids);
  };
  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  // Ручное задание номера очереди (1-based от левого верхнего угла).
  const handleOrderChange = (id: number, value: string) => {
    const n = parseInt(value, 10);
    const ids = sortedTests().map((x) => x.id);
    const from = ids.indexOf(id);
    if (from < 0 || !Number.isFinite(n)) return;
    const to = Math.min(Math.max(n - 1, 0), ids.length - 1);
    ids.splice(from, 1);
    ids.splice(to, 0, id);
    setOrderIds(ids);
    void saveDisplay(cardScale, ids);
  };

  if (loading) {
    return (
      <TestsPageContainer>
        <LoadingMessage>{t('loading')}</LoadingMessage>
      </TestsPageContainer>
    );
  }

  if (error) {
    return (
      <TestsPageContainer>
        <ErrorMessage>{t('errorPrefix')}: {error}</ErrorMessage>
      </TestsPageContainer>
    );
  }

  const isAdmin = user?.role === 'admin';
  const isUser = user?.role === 'user';

  return (
    <TestsPageContainer>
      <Header>
        <div>
          <Title>{t('pageTitle')}</Title>
          {user && (
            <p>
              {t('greeting')}, {user.username}!{' '}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          {isUser && (
            <Button variant="outline" size="md" onClick={() => navigate('/my-results')}>
              {t('navMyResults')}
            </Button>
          )}
          {isAdmin && (
            <>
              <Button variant="outline" size="md" onClick={() => navigate('/users')}>
                {t('navUsers')}
              </Button>
              <Button variant="outline" size="md" onClick={() => navigate('/results')}>
                {t('navResults')}
              </Button>
              <Button variant="outline" size="md" onClick={() => navigate('/conclusions')}>
                {t('navConclusions')}
              </Button>
              <Button variant="outline" size="md" onClick={() => navigate('/db-transfer')}>
                {t('navDbTransfer')}
              </Button>
              <Button variant="outline" size="md" onClick={() => navigate('/media')}>
                {t('navMedia')}
              </Button>
              <Button variant="secondary" size="md" onClick={handleCreateTest}>
                {t('createTest')}
              </Button>
              <label style={{ cursor: 'pointer' }}>
                <Button variant="outline" size="md" as="span">
                  {t('importTest')}
                </Button>
                <input
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleImportTest}
                />
              </label>
            </>
          )}

          <Button variant="outline" size="md" onClick={handleLogout}>
            {t('logout')}
          </Button>
        </div>
      </Header>

      {isAdmin && (
        <DisplayControls>
          <ControlRow>
            <label htmlFor="card-scale">{t('testsDisplayScaleLabel')}</label>
            <ScaleInput
              id="card-scale"
              type="range"
              min="0.6"
              max="1.6"
              step="0.05"
              value={cardScale}
              disabled={savingDisplay}
              onChange={(e) => commitScale(Number(e.target.value))}
            />
            <span>{Math.round(cardScale * 100)}%</span>
          </ControlRow>
          <ControlRow>
            <span>{t('testsDisplayDragHint')}</span>
          </ControlRow>
          {savingDisplay && <span style={{ fontSize: '0.85rem', color: '#666' }}>{t('testsDisplaySaving')}</span>}
        </DisplayControls>
      )}

      <TestsGrid $scale={cardScale}>
        {sortedTests().map((test, index) => {
          const visibleCount = test.questions?.filter((q) => !q.is_hidden).length ?? 0;
          const canStart = isAdmin ? visibleCount > 0 : (test.questions?.length ?? 0) > 0;

          return (
            <TestCardContainer
              key={test.id}
              $index={index}
              draggable={isAdmin}
              onDragStart={() => isAdmin && handleDragStart(test.id)}
              onDragOver={(e) => isAdmin && handleDragOver(e, test.id)}
              onDrop={(e) => isAdmin && handleDrop(e, test.id)}
              onDragEnd={handleDragEnd}
              style={{ opacity: draggedId === test.id ? 0.5 : 1, outline: dragOverId === test.id ? '2px dashed #2196F3' : 'none' }}
            >
              <CardBody>
                <TestHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 auto', minWidth: 0 }}>
                    {isAdmin && (
                      <DragHandle aria-label={t('testsDisplayDragHandle')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/>
                          <circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/>
                          <circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/>
                        </svg>
                      </DragHandle>
                    )}
                    <TestTitle>{test.title}</TestTitle>
                  </div>

                  {isAdmin && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <OrderInput
                        type="number"
                        min="1"
                        value={index + 1}
                        title={t('testsDisplayOrderTitle')}
                        onChange={(e) => handleOrderChange(test.id, e.target.value)}
                      />
                      <EditIconButton
                        onClick={() => handleEditTest(test.id)}
                        title={t('editTitle')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </EditIconButton>
                      <DeleteIconButton
                        onClick={() => handleDeleteTest(test.id, test.title)}
                        title={t('deleteTitle')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                      </DeleteIconButton>
                    </div>
                  )}
                </TestHeader>

                <Description>{test.description}</Description>

                <TestInfo>
                  {isAdmin && (
                    <>
                      <InfoRow>
                        <span>{t('stimuliLabel')}:</span>
                        <span>{test.questions?.length || 0}</span>
                      </InfoRow>
                      <InfoRow>
                        <span>{t('publishedLabel')}:</span>
                        <span>{test.is_published ? t('publishedYes') : t('publishedNo')}</span>
                      </InfoRow>
                    </>
                  )}
                </TestInfo>
              </CardBody>

              <StartButton
                variant="primary"
                size="md"
                onClick={() => handleStartTest(test.id)}
                disabled={!canStart}
                style={{ marginTop: '12px' }}
                title={!canStart ? t('noStimuli') : undefined}
              >
                {t('startTest')}
              </StartButton>

              {isAdmin && (
                <>
                  <AdminActions style={{ marginTop: '8px' }}>
                    {test.is_published ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnpublishTest(test.id)}
                        style={{ width: '100%' }}
                      >
                        {t('unpublish')}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePublishTest(test.id)}
                        disabled={visibleCount === 0}
                        style={{ width: '100%' }}
                        title={visibleCount === 0 ? t('noVisibleStimuli') : undefined}
                      >
                        {t('publish')}
                      </Button>
                    )}
                  </AdminActions>
                  <AdminActions style={{ marginTop: '8px' }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportTest(test.id, test.title)}
                      style={{ width: '100%' }}
                    >
                      {t('exportTest')}
                    </Button>
                  </AdminActions>
                </>
              )}
            </TestCardContainer>
          );
        })}
      </TestsGrid>

      {/* ── Toast ── */}
      {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}

      {/* ── Confirm modal ── */}
      {confirmState && (
        <Overlay onClick={() => setConfirmState(null)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>{confirmState.message}</p>
            <ConfirmActions>
              <Button variant="outline" size="sm" onClick={() => setConfirmState(null)}>
                {t('cancelButton')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  confirmState.onConfirm();
                  setConfirmState(null);
                }}
              >
                {t('conclusionsConfirmAction')}
              </Button>
            </ConfirmActions>
          </ConfirmModal>
        </Overlay>
      )}
    </TestsPageContainer>
  );
};

export default TestsPage;
